import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserCheck, 
  UserX, 
  X, 
  Shield, 
  Mail, 
  MoreHorizontal, 
  Download, 
  Plus, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Search, 
  Edit 
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  createUserProfile, 
  updateUserProfile, 
  deleteUserProfile,
  getUserProfile,
  getAllUsers,
  bulkUpdateUsers,
  exportUserData,
  importUserData
} from '../../firebase/collections';

const UserManagement = () => {
  const { adminData, logAdminAction } = useRealtime();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentUser, setCurrentUser] = useState({
    uid: '',
    email: '',
    displayName: '',
    role: 'user',
    isActive: true,
    permissions: [],
    preferences: {
      examType: '',
      subjects: [],
      notifications: true
    },
    profile: {
      phone: '',
      location: '',
      education: '',
      experience: '',
      avatar: ''
    }
  });

  const roles = [
    { value: 'user', label: 'User', color: 'bg-blue-100 text-blue-800' },
    { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-800' },
    { value: 'superadmin', label: 'Super Admin', color: 'bg-red-100 text-red-800' },
    { value: 'founder', label: 'Founder', color: 'bg-green-100 text-green-800' }
  ];

  const permissions = [
    { value: 'user_management', label: 'User Management' },
    { value: 'test_management', label: 'Test Management' },
    { value: 'content_management', label: 'Content Management' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'system_settings', label: 'System Settings' },
    { value: 'admin_management', label: 'Admin Management' },
    { value: 'financial_management', label: 'Financial Management' }
  ];

  const examTypes = ['CAT', 'JEE', 'NEET', 'GATE', 'UPSC', 'Banking'];
  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'General Knowledge'];

  // Load real users from Firebase
  useEffect(() => {
    if (adminData.users) {
      setUsers(adminData.users);
      setLoading(false);
    }
  }, [adminData.users]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const userData = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        role: currentUser.role,
        isActive: currentUser.isActive,
        permissions: currentUser.permissions,
        preferences: currentUser.preferences,
        profile: currentUser.profile,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: null,
        stats: {
          testsTaken: 0,
          averageScore: 0,
          totalTime: 0,
          questionsAnswered: 0,
          correctAnswers: 0
        }
      };

      const result = await createUserProfile(userData);
      if (result.success) {
        setSuccess('User created successfully');
        setShowCreateForm(false);
        resetForm();
        await logAdminAction('user_created', { userId: userData.uid, email: userData.email });
      } else {
        setError('Failed to create user: ' + result.error);
      }
    } catch (error) {
      setError('Error creating user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const updateData = {
        displayName: currentUser.displayName,
        role: currentUser.role,
        isActive: currentUser.isActive,
        permissions: currentUser.permissions,
        preferences: currentUser.preferences,
        profile: currentUser.profile,
        updatedAt: new Date().toISOString()
      };

      const result = await updateUserProfile(currentUser.uid, updateData);
      if (result.success) {
        setSuccess('User updated successfully');
        setEditingUser(null);
        resetForm();
        await logAdminAction('user_updated', { userId: currentUser.uid, email: currentUser.email });
      } else {
        setError('Failed to update user: ' + result.error);
      }
    } catch (error) {
      setError('Error updating user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await deleteUserProfile(userId);
      if (result.success) {
        setSuccess('User deleted successfully');
        await logAdminAction('user_deleted', { userId });
      } else {
        setError('Failed to delete user: ' + result.error);
      }
    } catch (error) {
      setError('Error deleting user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      setError('Please select users to perform bulk action');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let updateData = {};
      
      switch (action) {
        case 'activate':
          updateData = { isActive: true };
          break;
        case 'deactivate':
          updateData = { isActive: false };
          break;
        case 'delete':
          if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
            setLoading(false);
            return;
          }
          // Handle bulk delete
          for (const userId of selectedUsers) {
            await deleteUserProfile(userId);
          }
          setSuccess(`${selectedUsers.length} users deleted successfully`);
          setSelectedUsers([]);
          setLoading(false);
          return;
        default:
          setError('Invalid bulk action');
          setLoading(false);
          return;
      }

      const result = await bulkUpdateUsers(selectedUsers, updateData);
      if (result.success) {
        setSuccess(`Bulk action '${action}' completed successfully`);
        setSelectedUsers([]);
        await logAdminAction('bulk_user_action', { action, count: selectedUsers.length });
      } else {
        setError('Failed to perform bulk action: ' + result.error);
      }
    } catch (error) {
      setError('Error performing bulk action: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      const result = await exportUserData(users);
      if (result.success) {
        // Create and download CSV file
        const csvContent = result.data;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        setSuccess('Users exported successfully');
        await logAdminAction('users_exported', { count: users.length });
      } else {
        setError('Failed to export users: ' + result.error);
      }
    } catch (error) {
      setError('Error exporting users: ' + error.message);
    }
  };

  const resetForm = () => {
    setCurrentUser({
      uid: '',
      email: '',
      displayName: '',
      role: 'user',
      isActive: true,
      permissions: [],
      preferences: {
        examType: '',
        subjects: [],
        notifications: true
      },
      profile: {
        phone: '',
        location: '',
        education: '',
        experience: '',
        avatar: ''
      }
    });
  };

  const editUser = (user) => {
    setCurrentUser({
      uid: user.uid || user.id,
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      isActive: user.isActive,
      permissions: user.permissions || [],
      preferences: user.preferences || {
        examType: '',
        subjects: [],
        notifications: true
      },
      profile: user.profile || {
        phone: '',
        location: '',
        education: '',
        experience: '',
        avatar: ''
      }
    });
    setEditingUser(user);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus = !filterStatus || 
                         (filterStatus === 'active' && user.isActive) ||
                         (filterStatus === 'inactive' && !user.isActive);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.uid || user.id));
    }
  };

  const getRoleColor = (role) => {
    const roleInfo = roles.find(r => r.value === role);
    return roleInfo ? roleInfo.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (isActive) => {
    return isActive ? 
      <CheckCircle className="text-green-500" size={16} /> : 
      <XCircle className="text-red-500" size={16} />;
  };

  const getActivityLevel = (user) => {
    const lastLogin = user.lastLoginAt ? new Date(user.lastLoginAt) : null;
    const now = new Date();
    const daysSinceLogin = lastLogin ? Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24)) : 999;
    
    if (daysSinceLogin <= 1) return { level: 'Very Active', color: 'text-green-600', bg: 'bg-green-100' };
    if (daysSinceLogin <= 7) return { level: 'Active', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (daysSinceLogin <= 30) return { level: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { level: 'Inactive', color: 'text-red-600', bg: 'bg-red-100' };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-2">
              Manage user accounts, permissions, and preferences
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              <MoreHorizontal className="mr-2" size={16} />
              Bulk Actions
            </button>
            <button
              onClick={handleExportUsers}
              className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              <Download className="mr-2" size={16} />
              Export
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Plus className="mr-2" size={16} />
              Add User
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong>Error:</strong> {error}
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-700 hover:text-red-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            <strong>Success:</strong> {success}
            <button 
              onClick={() => setSuccess(null)}
              className="ml-4 text-green-700 hover:text-green-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        {showBulkActions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-lg shadow p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Actions</h3>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {selectedUsers.length} users selected
              </span>
              <button
                onClick={() => handleBulkAction('activate')}
                disabled={selectedUsers.length === 0}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
              >
                <UserCheck className="mr-2 inline" size={16} />
                Activate
              </button>
              <button
                onClick={() => handleBulkAction('deactivate')}
                disabled={selectedUsers.length === 0}
                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
              >
                <UserX className="mr-2 inline" size={16} />
                Deactivate
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                disabled={selectedUsers.length === 0}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                <Trash2 className="mr-2 inline" size={16} />
                Delete
              </button>
            </div>
          </motion.div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterRole('');
                  setFilterStatus('');
                }}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stats
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      Loading users...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const activityLevel = getActivityLevel(user);
                    return (
                      <tr key={user.uid || user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.uid || user.id)}
                            onChange={() => handleSelectUser(user.uid || user.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <img
                                className="h-10 w-10 rounded-full"
                                src={user.profile?.avatar || `https://ui-avatars.com/api/?name=${user.displayName}&background=random`}
                                alt=""
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.displayName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                            {roles.find(r => r.value === user.role)?.label || user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(user.isActive)}
                            <span className="ml-2 text-sm text-gray-900">
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${activityLevel.bg} ${activityLevel.color}`}>
                            {activityLevel.level}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="space-y-1">
                            <div>Tests: {user.stats?.testsTaken || 0}</div>
                            <div>Avg Score: {user.stats?.averageScore || 0}%</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => editUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.uid || user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit User Modal */}
        {(showCreateForm || editingUser) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingUser ? 'Edit User' : 'Create New User'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={currentUser.email}
                      onChange={(e) => setCurrentUser({...currentUser, email: e.target.value})}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                    <input
                      type="text"
                      value={currentUser.displayName}
                      onChange={(e) => setCurrentUser({...currentUser, displayName: e.target.value})}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      value={currentUser.role}
                      onChange={(e) => setCurrentUser({...currentUser, role: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={currentUser.isActive}
                      onChange={(e) => setCurrentUser({...currentUser, isActive: e.target.value === 'true'})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {permissions.map(permission => (
                      <label key={permission.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={currentUser.permissions.includes(permission.value)}
                          onChange={(e) => {
                            const newPermissions = e.target.checked
                              ? [...currentUser.permissions, permission.value]
                              : currentUser.permissions.filter(p => p !== permission.value);
                            setCurrentUser({...currentUser, permissions: newPermissions});
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{permission.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingUser(null);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
